import React, { Fragment, useState, useMemo } from 'react';
import { Row, Col, Card, Badge, Button, Dropdown } from 'react-bootstrap';
import { useQuery, useMutation } from '@apollo/client/react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Notify } from 'notiflix';

import { GET_ALL_EVENTS } from 'helper/graphql/queries';
import { DELETE_EVENT, SEND_EVENT_FEEDBACK_EMAILS, SEND_EVENT_CERTIFICATES } from 'helper/graphql/mutations';
import TanstackTable from 'components/elements/advance-table/TanstackTable';
import AlertModal from 'components/elements/modal-alert/AlertModal';
import CreateEditEventModal from './CreateEditEventModal';
import EventRegistrantsModal from './EventRegistrantsModal';
import EventFeedbackModal from './EventFeedbackModal';

const formatTypeLabel = (t) =>
	t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';

const ActionMenu = ({ event, onEdit, onDelete, onSendFeedback, onSendCertificates, onViewRegistrants, onViewFeedback }) => {
	const CustomToggle = React.forwardRef(({ onClick }, ref) => (
		<Link
			to="#"
			ref={ref}
			onClick={(e) => { e.preventDefault(); onClick(e); }}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			<i className="fe fe-more-vertical"></i>
		</Link>
	));

	return (
		<Dropdown>
			<Dropdown.Toggle as={CustomToggle} />
			<Dropdown.Menu align="end">
				<Dropdown.Item onClick={() => onViewRegistrants(event)}>
					<i className="fe fe-users me-2 text-muted"></i>View Registrants
				</Dropdown.Item>
				<Dropdown.Item onClick={() => onViewFeedback(event)}>
					<i className="fe fe-message-square me-2 text-muted"></i>View Feedback
				</Dropdown.Item>
				<Dropdown.Item onClick={() => onEdit(event)}>
					<i className="fe fe-edit-2 me-2 text-muted"></i>Edit
				</Dropdown.Item>
				<Dropdown.Item onClick={() => onSendFeedback(event)}>
					<i className="fe fe-mail me-2 text-muted"></i>Send Feedback Emails
				</Dropdown.Item>
				<Dropdown.Item onClick={() => onSendCertificates(event)}>
					<i className="fe fe-award me-2 text-muted"></i>Send Certificates
				</Dropdown.Item>
				<Dropdown.Divider />
				<Dropdown.Item className="text-danger" onClick={() => onDelete(event)}>
					<i className="fe fe-trash-2 me-2"></i>Delete
				</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);
};

const AllEvents = () => {
	const [showCreate, setShowCreate] = useState(false);
	const [editEvent, setEditEvent] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [feedbackTarget, setFeedbackTarget] = useState(null);
	const [certificatesTarget, setCertificatesTarget] = useState(null);
	const [registrantsEvent, setRegistrantsEvent] = useState(null);
	const [feedbackEvent, setFeedbackEvent] = useState(null);

	const { data, loading, refetch } = useQuery(GET_ALL_EVENTS, {
		// Fetch all events (no isActive filter) for admin
		variables: {},
	});

	const [onDelete, { loading: deleting }] = useMutation(DELETE_EVENT);
	const [onSendFeedback, { loading: sendingFeedback }] = useMutation(SEND_EVENT_FEEDBACK_EMAILS);
	const [onSendCertificates, { loading: sendingCertificates }] = useMutation(SEND_EVENT_CERTIFICATES);

	const events = useMemo(
		() => data?.allEvents?.edges?.map((e) => e.node) || [],
		[data]
	);

	const upcomingCount = events.filter((e) => new Date(e.sessionDatetime) > new Date()).length;
	const activeCount = events.filter((e) => e.isActive).length;

	const handleDelete = async () => {
		try {
			await onDelete({ variables: { guid: deleteTarget.guid } });
			Notify.success('Event deleted.');
			setDeleteTarget(null);
			refetch();
		} catch (err) {
			Notify.failure(err?.message || 'Delete failed.');
		}
	};

	const handleSendFeedback = async () => {
		try {
			console.log('sending feedback emails for event', feedbackTarget);
			const res = await onSendFeedback({ variables: { eventGuid: feedbackTarget.guid } });
			console.log('send feedback res', res);
			const count = res?.data?.sendFeedbackEmails?.sentCount ?? 0;
			Notify.success(`Feedback emails sent to ${count} participant${count !== 1 ? 's' : ''}.`);
			setFeedbackTarget(null);
		} catch (err) {
			Notify.failure(err?.message || 'Failed to send emails.');
		}
	};

	const handleSendCertificates = async () => {
		try {
			const res = await onSendCertificates({ variables: { eventGuid: certificatesTarget.guid } });
			console.log('send certificates res', res);
			const count = res?.data?.sendEventCertificates?.sentCount ?? 0;
			Notify.success(`Certificates sent to ${count} participant${count !== 1 ? 's' : ''}.`);
			setCertificatesTarget(null);
		} catch (err) {
			Notify.failure(err?.message || 'Failed to send certificates.');
		}
	};

	const columns = useMemo(() => [
		{
			header: 'Event',
			accessorKey: 'title',
			cell: ({ getValue, row }) => (
				<div>
					<p className="ae-event-title mb-1">{getValue()}</p>
					<Badge bg="" className="ae-type-badge">
						{formatTypeLabel(row.original.eventType)}
					</Badge>
				</div>
			),
		},
		{
			header: 'Date & Time',
			accessorKey: 'sessionDatetime',
			cell: ({ getValue }) => {
				const d = moment(getValue()).utcOffset(180);
				return (
					<div className="ae-date-cell">
						<span className="ae-date-primary">{d.format('MMM D, YYYY')}</span>
						<span className="ae-date-secondary">{d.format('h:mm A')} · {d.fromNow()}</span>
					</div>
				);
			},
		},
		{
			header: 'Speaker',
			accessorKey: 'speakerName',
			cell: ({ getValue, row }) => getValue() ? (
				<div>
					<span className="ae-speaker-name">{getValue()}</span>
					{row.original.speakerTitle && (
						<span className="ae-speaker-title d-block">{row.original.speakerTitle}</span>
					)}
				</div>
			) : <span className="text-muted">—</span>,
		},
		{
			header: 'Registrations',
			accessorKey: 'registrationCount',
			cell: ({ getValue, row }) => {
				const count = getValue() || 0;
				const max = row.original.maxParticipants;
				return (
					<button
						className="ae-reg-btn"
						onClick={() => setRegistrantsEvent(row.original)}
						title="View registrants"
					>
						<span className="ae-reg-count">{count}</span>
						{max > 0 && <span className="ae-reg-max"> / {max}</span>}
						<i className="fe fe-users ae-reg-icon ms-1"></i>
					</button>
				);
			},
		},
		{
			header: 'Status',
			accessorKey: 'isActive',
			cell: ({ getValue, row }) => {
				const isUpcoming = new Date(row.original.sessionDatetime) > new Date();
				return (
					<div className="d-flex flex-column gap-1">
						<Badge bg="" className={getValue() ? 'ae-badge-active' : 'ae-badge-inactive'}>
							{getValue() ? 'Published' : 'Unpublished'}
						</Badge>
						<Badge bg="" className={isUpcoming ? 'ae-badge-upcoming' : 'ae-badge-past'}>
							{isUpcoming ? 'Upcoming' : 'Past'}
						</Badge>
					</div>
				);
			},
		},
		{
			id: 'actions',
			header: '',
			cell: ({ row }) => (
				<ActionMenu
					event={row.original}
					onEdit={(ev) => setEditEvent(ev)}
					onDelete={(ev) => setDeleteTarget(ev)}
					onSendFeedback={(ev) => setFeedbackTarget(ev)}
					onSendCertificates={(ev) => setCertificatesTarget(ev)}
					onViewRegistrants={(ev) => setRegistrantsEvent(ev)}
				onViewFeedback={(ev) => setFeedbackEvent(ev)}
				/>
			),
		},
	], []);

	return (
		<Fragment>
			{/* Stats */}
			<Row className="g-3 mb-4">
				{[
					{ label: 'Total Events', value: events.length, icon: 'fe-calendar', color: '#1d9eb0' },
					{ label: 'Upcoming', value: upcomingCount, icon: 'fe-clock', color: '#F68521' },
					{ label: 'Published', value: activeCount, icon: 'fe-eye', color: '#10b981' },
				].map((stat) => (
					<Col key={stat.label} lg={4} md={6}>
						<Card className="ae-stat-card">
							<Card.Body className="p-4 d-flex align-items-center justify-content-between">
								<div>
									<div className="ae-stat-label">{stat.label}</div>
									<div className="ae-stat-value">{stat.value}</div>
								</div>
								<div className="ae-stat-icon" style={{ background: `${stat.color}18`, color: stat.color }}>
									<i className={`fe ${stat.icon}`}></i>
								</div>
							</Card.Body>
						</Card>
					</Col>
				))}
			</Row>

			{/* Table card */}
			<Card className="ae-table-card">
				<Card.Header className="ae-table-header d-flex align-items-center justify-content-between">
					<div>
						<h5 className="mb-1">All Events</h5>
						<p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
							Create and manage X Space and clinical case sessions
						</p>
					</div>
					<Button className="ae-create-btn" onClick={() => setShowCreate(true)}>
						<i className="fe fe-plus me-2"></i>New Event
					</Button>
				</Card.Header>
				<Card.Body className="p-0">
					<TanstackTable
						data={events}
						columns={columns}
						filter={true}
						filterPlaceholder="Search events..."
						pagination={true}
					/>
				</Card.Body>
			</Card>

			{/* Create / Edit modal */}
			<CreateEditEventModal
				show={showCreate || !!editEvent}
				onHide={() => { setShowCreate(false); setEditEvent(null); }}
				event={editEvent}
				eventsWithSchemas={
					[...events]
						.filter(e => e.feedbackFormSchema)
						.sort((a, b) => new Date(b.sessionDatetime) - new Date(a.sessionDatetime))
						.map(e => ({ guid: e.guid, title: e.title, feedbackFormSchema: e.feedbackFormSchema }))
				}
				onSaved={refetch}
			/>

			{/* Delete confirm */}
			<AlertModal
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				title="Delete Event"
				message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
				handleYes={handleDelete}
				isLoading={deleting}
			/>

			{/* Send feedback confirm */}
			<AlertModal
				isOpen={!!feedbackTarget}
				onClose={() => setFeedbackTarget(null)}
				title="Send Feedback Emails"
				message={`Send the post-session feedback form to all registered participants of "${feedbackTarget?.title}"?`}
				handleYes={handleSendFeedback}
				isLoading={sendingFeedback}
			/>

			{/* Send certificates confirm */}
			<AlertModal
				isOpen={!!certificatesTarget}
				onClose={() => setCertificatesTarget(null)}
				title="Send Certificates"
				message={`Send e-certificates of attendance to all participants of "${certificatesTarget?.title}"?`}
				handleYes={handleSendCertificates}
				isLoading={sendingCertificates}
			/>

			{/* Registrants modal */}
			<EventRegistrantsModal
				show={!!registrantsEvent}
				onHide={() => setRegistrantsEvent(null)}
				event={registrantsEvent}
			/>

			{/* Feedback modal */}
			<EventFeedbackModal
				show={!!feedbackEvent}
				onHide={() => setFeedbackEvent(null)}
				event={feedbackEvent}
			/>

			<style>{`
				.ae-stat-card {
					border: none;
					border-radius: 12px;
					box-shadow: 0 2px 8px rgba(0,0,0,0.06);
				}
				.ae-stat-label {
					font-size: 0.8125rem;
					color: #94a3b8;
					font-weight: 600;
					text-transform: uppercase;
					letter-spacing: 0.4px;
					margin-bottom: 0.4rem;
				}
				.ae-stat-value {
					font-size: 2rem;
					font-weight: 800;
					color: #1e293b;
					line-height: 1;
				}
				.ae-stat-icon {
					width: 52px;
					height: 52px;
					border-radius: 12px;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 1.5rem;
				}
				.ae-table-card {
					border: none;
					border-radius: 16px;
					box-shadow: 0 4px 20px rgba(0,0,0,0.08);
					overflow: hidden;
				}
				.ae-table-header {
					background: white;
					border-bottom: 2px solid #f1f5f9;
					padding: 1.25rem 1.5rem;
				}
				.ae-table-header h5 {
					font-size: 1.0625rem;
					font-weight: 700;
					color: #1e293b;
					margin: 0;
				}
				.ae-create-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border: none;
					color: white;
					font-weight: 600;
					font-size: 0.875rem;
					padding: 0.5rem 1.25rem;
					border-radius: 10px;
					display: flex;
					align-items: center;
					white-space: nowrap;
				}
				.ae-create-btn:hover { opacity: 0.9; color: white; background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%); }
				.ae-event-title { font-size: 0.9rem; font-weight: 700; color: #1e293b; margin: 0; }
				.ae-type-badge {
					background: rgba(29,158,176,0.1) !important;
					color: #1d9eb0 !important;
					font-size: 0.6875rem;
					font-weight: 700;
					padding: 3px 8px;
					border-radius: 6px;
				}
				.ae-date-cell { display: flex; flex-direction: column; gap: 0.2rem; }
				.ae-date-primary { font-size: 0.875rem; font-weight: 600; color: #334155; }
				.ae-date-secondary { font-size: 0.75rem; color: #94a3b8; }
				.ae-speaker-name { font-size: 0.875rem; font-weight: 600; color: #334155; }
				.ae-speaker-title { font-size: 0.75rem; color: #94a3b8; }
				.ae-reg-btn { background: none; border: none; padding: 0; display: flex; align-items: baseline; gap: 0.1rem; cursor: pointer; }
				.ae-reg-btn:hover .ae-reg-count { color: #1d9eb0; }
				.ae-reg-btn:hover .ae-reg-icon { opacity: 1; }
				.ae-reg-count { font-size: 1.125rem; font-weight: 800; color: #1e293b; }
				.ae-reg-max { font-size: 0.8125rem; color: #94a3b8; }
				.ae-reg-icon { font-size: 0.75rem; color: #94a3b8; opacity: 0; transition: opacity 0.15s; align-self: center; }
				.ae-badge-active { background: rgba(16,185,129,0.1) !important; color: #10b981 !important; font-size: 0.6875rem; font-weight: 700; padding: 3px 8px; border-radius: 6px; }
				.ae-badge-inactive { background: rgba(148,163,184,0.1) !important; color: #94a3b8 !important; font-size: 0.6875rem; font-weight: 700; padding: 3px 8px; border-radius: 6px; }
				.ae-badge-upcoming { background: rgba(246,133,33,0.1) !important; color: #F68521 !important; font-size: 0.6875rem; font-weight: 700; padding: 3px 8px; border-radius: 6px; }
				.ae-badge-past { background: rgba(100,116,139,0.1) !important; color: #64748b !important; font-size: 0.6875rem; font-weight: 700; padding: 3px 8px; border-radius: 6px; }
			`}</style>
		</Fragment>
	);
};

export default AllEvents;
